import { ConditionParams } from "@multiweb/clients/dataReader";
import { CaseInsensitiveMap } from "../../../../../Global/Base/DataTypes/CaseInsensitiveMap";
import { Event } from "../../../../../Global/Base/Events";
import { CancellationToken } from "../../../../../Global/Base/Promises/CancellationTokenSource";
import { ConditionOperator, StringUtils } from "../../../../../Global/Base/Utils/StringUtils";
import { TimerUtils } from "../../../../../Global/Base/Utils/TimerUtils";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { RequestContext } from "../../../../../OW.Remoting/Remoting/RequestContext";
import { Control } from "../../../../UI.BaseControls/Control";
import { GenericControl } from "../../../../UI.BaseControls/GenericControl";
import { IBooleanDetailValueEventData } from "../../../../UI.BaseControls/IEvent";
import { registerCustomElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { convertContextFromFrameToRequest } from "../../../Utilities/SharedUIMethods";
import { LocalizedStringBuilder } from "../../DataBuilders/LocalizedStringBuilder";
import { MessageWindow } from "../../MessageWindow";
import { UIManager } from "../../UIManager";
import { FrameContext } from "../../UIManagerInterfaces";
import { AdvancedFilterEdit } from "./AdvancedFilters/AdvancedFilterEdit";
import { FilterGroup } from "./FilterGroup";
import { FilterBlockAttrNameToData, FilterBlockAttrNameToDataMap, FilterTargetType, IFilterBlock, IFilterBlockExecutedEventArgs, IFilterJsonData, IFilterJsonDataAttr, IFilterJsonResultValue } from "./FilterInterfaces";
import { FilterRegistrations } from "./FilterRegistrations";
import { FilterValidator } from "./FilterValidator";
import { IFilterManager } from "./IFilterManager";

export class FilterBlock extends Control implements IFilterBlock {
    // #region Events
    /** Událost volaná při změně podmínky na frame */
    public frameConditionChanged: Event<IFilterManager, string> = new Event<IFilterManager, string>();
    /** Událost volaná při změně stavu filtru */
    public filterSaveStateChanged: Event<FilterBlock, boolean> = new Event<FilterBlock, boolean>();
    public filterExecuted: Event<FilterBlock, IFilterBlockExecutedEventArgs> = new Event<FilterBlock, IFilterBlockExecutedEventArgs>();
    /** Událost volaná při odstranění FilterGroup z FilterBlocku. Jako parametr se předává, zda jde o dočasné, nebo trvalé odstranění (při odstraňování FilterGroup je po určitý časový interval možné vrátit filtr zpět) */
    public filterGroupRemoved: Event<FilterGroup, {temporarily: boolean}> = new Event<FilterGroup, {temporarily: boolean}>();
    /** Událost volaná při obnově odstraněné FilterGroup, viz událost filterGroupRemoved s parametrem temporarily=true */
    public filterGroupRestored: Event<FilterGroup, void> = new Event<FilterGroup, void>();
    // #endregion Events

    private _advancedFilter: AdvancedFilterEdit;
    private _frameName: string = "";
    private _filterName: string;
    private _filterId: string;
    private _parentBind: FilterBlock;
    private _binding: FilterBlock;
    private _attrNameToDataType: FilterBlockAttrNameToDataMap;
    private _noUnsaveEventBinding: boolean;
    private _executeRequest: boolean = false;
    private _isUsed: boolean = false;
    private _isSaved: boolean = false;
    private _executeCount: number = 0;

    //#region Listeners
    private _saveStateListener: (filterBlock: FilterBlock, saved: boolean) => void;
    //#endregion Listeners

    /**
     * Nazev formulare
     */
    public formName: string = "";
    /** Kontext framu. (Setuje se ve `FilterManager`.) */
    public frameContext: FrameContext | undefined = undefined;
    public objectName: string = "";
    public frameCondition: string; //Podmínka framu z definice. Slouží k tomu, aby při načítání a vyhledvání hodnot se nevraceli data, které nesplňují podmínku framu.
    public frameConditionParams: ConditionParams;
    public disableValueShortening: boolean;
    public type: FilterTargetType;
    public currentUserId: number | null = null;

    private _isImplementationFilter: boolean = false;

    public get isImplementationFilter(): boolean {
        return this._isImplementationFilter;
    }
    public set isImplementationFilter(value: boolean) {
        this._isImplementationFilter = value;
    }

    public get executeCount(): number {
        if (this._parentBind) return this._parentBind._executeCount;
        return this._executeCount;
    }
    /**
     * Dočasný název před uložením
     */
    public tempName: string = "";

    public get executeRequest(): boolean {
        return this._executeRequest;
    }

    public set executeRequest(v: boolean) {
        this._executeRequest = v;
        if (v) {
            TimerUtils.waitOrCancel(`_exec_req_combo_${this._innerId}`, () => { // `
                if (this._executeRequest) {
                    this._executeRequest = false;

                    this.execute();
                }
            }, 500);
        }
    }

    /** Seznam atributů propoujících datový typ a lokalizaci */
    public get attrNameToDataType(): FilterBlockAttrNameToDataMap {
        return this._attrNameToDataType;
    }

    public set attrNameToDataType(v: FilterBlockAttrNameToDataMap) {
        this._attrNameToDataType = v;

        if (this._binding && this._attrNameToDataType !== this._binding._attrNameToDataType) {
            this._binding._attrNameToDataType = this._attrNameToDataType;
        }
    }

    public get bindingFilterBlock(): FilterBlock {
        return this._binding;
    }

    /**
     * Nazev Framu z formulare
     */
    public set frameName(v: string) {
        this._frameName = v;
        this.setAttribute("frame-name", v);
    }

    public get frameName(): string {
        return this._frameName;
    }

    /**
     * ID filtru jako ID objektu z UserProfile
     */
    public set filterId(v: string) {
        this._filterId = v;
    }

    public get filterId(): string {
        return this._filterId;
    }

    /**
     * Indikator, zda je block vybran jako aktivni
     */
    public set isUsed(v: boolean) {
        this._isUsed = v;
        this.callEvent("filterSelected", { value: v } as IBooleanDetailValueEventData);
    }

    public get isUsed(): boolean {
        return this._isUsed;
    }

    /**
     * Indikator ulozeni
     */
    public get isSaved(): boolean {
        return this._isSaved;
    }

    /**
     * Nazev filtru
     */
    public set filterName(v: string) {
        this._filterName = v;

        this.callEvent("filterNameChanged", { name: v });
    }

    public get filterName(): string {
        return this._filterName;
    }

    /**
     * Seznam filtracnich skupin v bloku
     */
    public get groups(): FilterGroup[] {
        return Array.from(this.querySelectorAll("tsc-FilterGroup")) as FilterGroup[];
    }

    public get parentFilterBlock(): FilterBlock {
        return this._parentBind;
    }

    /** Zda je nastaven pokročilý filtr */
    public get hasActiveAdvancedFilter(): boolean {
        return !StringUtils.isNullOrEmpty(this._advancedFilter.condition);
    }

    protected override _constructor(): void {
        super._constructor();

        this.type = FilterTargetType.inPanel;
        this.classList.add("FilterBlock");
        this._noUnsaveEventBinding = false;
        this._attrNameToDataType = new CaseInsensitiveMap<FilterBlockAttrNameToData>();

        this._advancedFilter = new AdvancedFilterEdit();
        this.appendChild(this._advancedFilter);

        this.afterAttach(() => {
            GenericControl.registerEventListener(this._advancedFilter, "executeFilter", () => {
                this.unsave();
                this.execute();
            });

            GenericControl.registerEventListener(this._advancedFilter, "filterChanged", () => this.unsave());
        });

        /**
         * FilterBlock je sdílené pro více sloupců. Zobrazují se ale v PopupMenu, které sdílené není a při zavření se odstraňuje z DOM.
         * FilterBlock je v tu chvíli potomkem, takže by se disposovala také, pokud by zůstal disposeOnDetach nastavený na true.
         */
        this.disposeOnDetach = false;
    }

    /**
     * Provede pribindovani k jinemu filterblocku, tzn. k vyovlani filtrovani se pouzije hlavni blok, ale filtry lze nastavovat i tady
     * @param block
     */
    public binding(block: FilterBlock): void {
        block._parentBind = this;
        this._binding = block;
        block.attrNameToDataType = this.attrNameToDataType;
        block.type = FilterTargetType.column;
        block._loadFromJson(this.getFilterData(this.type));

        this._saveStateListener = (_filterBlock, saved) => {
            if (this._noUnsaveEventBinding) {
                this._noUnsaveEventBinding = false;
                return;
            }

            if (!saved) {
                block._loadFromJson(this.getFilterData(this.type));
            }
        };

        this.filterSaveStateChanged.add(this._saveStateListener);

        block.filterSaveStateChanged.add((_filterBlock, saved) => {
            if (!saved) {
                this._loadFromJson(block.getFilterData(this.type));
                this._noUnsaveEventBinding = true;
                this.unsave();
            }
        });
    }

    /**
     * Přidá skupinu filtrů do bloku
     * @param group Přidávaná skupina
     */
    public addGroup(item: FilterGroup): void {
        item.parent = this;
        this.appendChild(item);

        // pridava se nova skupina nacte se vychozi typ operace, ktery se prida jako prvni polozka pro trideni, aby nezustala skupina prazdna
        item.createFilterItem(item.filterStructure.multiOperation ?
            { operation: item.filterStructure.multiOperation, isUsed: true } :
            { operation: FilterRegistrations.getDefaultOperation(item.filterStructure.dataType), isUsed: true });
    }

    public async execute(): Promise<void> {
        if (this._parentBind) { // Zajišťuje, že je filtrování spuštěno z bloku na panelu, na který reagují frame komponenty
            this._parentBind._execute(this.getFilterData(this._parentBind.type)); // Na _parentBind ještě nejsou data, takže je musíme předat
        } else {
            this._execute();
        }
    }

    public async getCurrentFilterData(): Promise<IFilterBlockExecutedEventArgs> {
        return this._execute(null, false);
    }

    /**
     * Vyvolá událost filterExecuted pro spuštění filtrování
     * @param filterData Data filtru
     */
    private async _execute(filterData: IFilterJsonData = this.getFilterData(this.type), withEvent = true): Promise<IFilterBlockExecutedEventArgs> {
        const advancedFilterCondition = await FilterValidator.validateAdvancedFilterCondition(this._advancedFilter.condition, this.objectName, this.attrNameToDataType, this._getRequestContext());
        this._executeCount++;

        if (filterData.attrs.length === 0) {
            this.filterExecuted.trigger(this, {
                condition: advancedFilterCondition,
                filteredAttrNames: null,
                filterData: filterData,
            });
            // this.callEvent("filterExecuted", {
            //     condition: advancedFilterCondition,
            //     columns: {},
            //     result: filterData
            // });

            return null;
        }

        const cond = FilterRegistrations.getServerCondition((s: string) => {
            return this.attrNameToDataType.get(s).dataType;
        }, filterData.attrs);

        let showErrorMessage: boolean = false;

        // nacteni seznamu sloupcu, pro informaci o tom, dle kterych sloupcu se filtruje
        const attrNamesToFilterBy: Map<string, boolean> = new Map<string, boolean>();
        for (const attr of filterData.attrs) {
            if (!attr.enabled)
                continue;
            let isUsed = false;
            // provadi se kontrola pokud jsou ve skupine zakazany vsechny polozky, nebo jsou nevyplneny ignoruji se
            for (const item of attr.conditions) {
                if (item.enabled) {
                    if (item.isValid) {
                        if (item.values && item.values.length > 0) {
                            for (const val of item.values) {
                                if (val.value) {
                                    isUsed = true;
                                    break;
                                }
                            }
                        } else {
                            isUsed = true;
                        }
                        //break;
                    } else {
                        showErrorMessage = true;
                        break;
                    }
                }
            }
            if (!isUsed)
                continue;

            attrNamesToFilterBy.set(attr.attrName, attr.enabled);
            //#region Pridani attributu bez vazby
            const point = attr.attrName.indexOf(".");
            if (point > 0) {
                const attrName = attr.attrName.substring(0, point);
                attrNamesToFilterBy.set(attrName, true);
            }
            //#endregion
        }

        if (showErrorMessage) {
            MessageWindow.showAlert(new LocalizedStringBuilder("GUI-762112")); // Zadejte platnou hodnotu filtru.
        }

        if (withEvent) {
            this.filterExecuted.trigger(this, {
                condition: StringUtils.combineConditions(ConditionOperator.and, cond, advancedFilterCondition),
                filteredAttrNames: attrNamesToFilterBy,
                filterData: filterData,
            });
        }

        return {
            condition: StringUtils.combineConditions(ConditionOperator.and, cond, advancedFilterCondition),
            filteredAttrNames: attrNamesToFilterBy,
            filterData: filterData,
        };
    }

    /**
     * Zpracuje data z filtru
     * @param isForExport Vrácený objekt, nebude mít definované property, které jsou defaultně true. (např. enabled)
     */
    public getFilterData(filterTargetType: FilterTargetType, isForExport: boolean = false, withAdvancedFilter: boolean = true): IFilterJsonData {
        const attrGroups: IFilterJsonDataAttr[] = [];

        for (const grp of this.groups) {
            if (grp.isRemoving)
                continue; // pokud je ve fazi odebirani nebude do dat pridan
            attrGroups.push(grp.getResult(isForExport, filterTargetType));
        }

        const filterData: IFilterJsonData = {
            saveInfo: !isForExport && (this._filterId || this.filterName) ? {
                id: this._filterId,
                name: this.filterName,
            } : undefined,
            attrs: attrGroups,
        };

        if (withAdvancedFilter) {
            filterData.advancedCondition = this._advancedFilter.condition;
        }

        return filterData;
    }

    /**
     * Uložení filtru na server
     * @param newName Název pro uložení
     * @param saveAs Uložit jako nový filtr
     *
     * @return ID objektu
     */
    public async save(newName: string, filterManager: IFilterManager, saveAs: boolean = false): Promise<FilterBlock> {
        if (!this._filterId) saveAs = false; // pokud filter neni jeste ulozen prepneme na standartni ukladani
        if (!saveAs && !filterManager.canEditImplementationFilters && this.isImplementationFilter)
            return this;

        let obj: OWObject = null;
        const sValueArr: Array<Promise<OWObject>> = [];
        const oldName = this.filterName;
        this.filterName = newName;

        UIManager.instance.showLoader();
        try {
            const editContextIdRequest = convertContextFromFrameToRequest(this.frameContext);
            const userID: number | null = filterManager.canEditImplementationFilters && this.isImplementationFilter ? 0 : this.currentUserId;

            // Vytvoří nebo načte záznam z tabulky UserProfile
            if (this._filterId && !saveAs) {
                obj = await OWManager.getObject(this._filterId, "UserProfile", undefined);
            } else {
                obj = await OWManager.newObject("UserProfile", undefined);

                sValueArr.push(obj.setValue("DataType", "FilterInfo"));
                sValueArr.push(obj.setValue("FormName", this.formName));
                sValueArr.push(obj.setValue("Name", this.frameName));
            }

            sValueArr.push(obj.setValue("User", userID));

            const advancedCondition = await FilterValidator.validateAdvancedFilterCondition(this._advancedFilter.condition, this.objectName, this.attrNameToDataType, editContextIdRequest);
            const withAdvancedFilter = this.hasActiveAdvancedFilter && !StringUtils.isNullOrEmpty(advancedCondition);
            const filterData = this.getFilterData(this.type, undefined, withAdvancedFilter);
            const filterJSONData = JSON.stringify(filterData);

            sValueArr.push(obj.setValue("Data", filterJSONData));
            sValueArr.push(obj.setValue("IsDefault", this._isUsed));

            await Promise.all(sValueArr);
            await obj.applyUpdates();

            const retId = obj.id;

            if (saveAs) {
                this._isSaved = true;
                await filterManager.getSavedFilterById(this.filterId);
                const filterBlock = filterManager.createNew(true) as FilterBlock;
                filterBlock._loadFromJson(filterData);
                filterBlock.isImplementationFilter = this.isImplementationFilter && filterManager.canEditImplementationFilters;
                filterBlock.filterName = newName;
                filterBlock._isSaved = true;
                filterBlock._filterId = retId;
                this.filterName = oldName;
                return filterBlock;
            } else {
                this._filterId = retId;
                this._isSaved = true;
                this.filterSaveStateChanged.trigger(this, this._isSaved);
                return this;
            }
        } finally {
            UIManager.instance.hideLoader();
            obj.dispose();
        }
    }

    /** Označí filtr jako modifikovaný a vyvolá událost o změně */
    public unsave(): void {
        this._isSaved = false;
        this.filterSaveStateChanged.trigger(this, this._isSaved);
    }

    /**
     * Nahraje filter s JSONu do tohoto i provázaného bloku
     */
    public loadData(json: string | IFilterJsonData): void {
        this._loadFromJson(json);
        this._binding._loadFromJson(json);
    }

    /**
     * Nahraje filter s JSONu do tohoto filter bloku
     */
    protected _loadFromJson(json: string | IFilterJsonData): void {
        let obj: IFilterJsonData;
        if (typeof json === "string")
            obj = JSON.parse(json as string);
        else
            obj = json as IFilterJsonData;

        for (let i = this.childNodes.length - 1; i >= 0; i--) {
            const node = this.childNodes[i];

            if (node instanceof FilterGroup) {
                this.removeChild(node);
            }
        }

        if (obj.enabled == undefined)
            obj.enabled = true;
        if (obj.saveInfo && obj.saveInfo.name) {
            this.filterName = obj.saveInfo.name;
            if (!this._isSaved) {
                this._isSaved = true;
                this.filterSaveStateChanged.trigger(this, this._isSaved);
            }
        }

        if (obj.advancedCondition && this.type === FilterTargetType.inPanel) {
            this._advancedFilter.showFilter(obj.advancedCondition);
        }

        for (const item of obj.attrs) {
            const dataTypeInfo = this.attrNameToDataType.get(item.attrName);
            const struct = FilterRegistrations.getDataTypeFilter(dataTypeInfo); // nacte struktury na zaklade datoveho typu
            if (struct == undefined)
                continue;
            const fgroup = new FilterGroup();
            fgroup.frameContext = this.frameContext;
            fgroup.attrName = item.attrName;
            fgroup.operator = item.operator;
            fgroup.parent = this;
            fgroup.setLocKey(dataTypeInfo.locKey, dataTypeInfo.locParams);
            fgroup.filterStructure = struct;
            fgroup.enabled = item.enabled == undefined ? true : item.enabled;
            fgroup.displayFormat = item.displayFormat;
            if (item.conditions) {
                for (const iitem of item.conditions) {
                    if (!struct.multiOperation) {
                        const struct_oper = FilterRegistrations.getOperationByOperationId(struct.dataType, iitem.operation);
                        if (struct_oper == undefined)
                            continue;

                        struct_oper.subId = iitem.subOperation;

                        const valuesLoad: { [id: string]: IFilterJsonResultValue } = {};

                        for (const val of iitem.values) {
                            valuesLoad[val.fieldId] = val;
                        }

                        fgroup.createFilterItem({
                            operation: struct_oper,
                            values: valuesLoad,
                            operator: iitem.operator,
                            isUsed: iitem.enabled == undefined ? true : iitem.enabled,
                            subOperationID: iitem.subOperation,
                        });
                    } else {
                        const valuesLoad: { [id: string]: IFilterJsonResultValue } = {};

                        for (const val of iitem.values) {
                            valuesLoad[val.fieldId] = val;
                        }

                        fgroup.createFilterItem({
                            operation: struct.multiOperation,
                            values: valuesLoad,
                            operator: iitem.operator,
                            isUsed: iitem.enabled == undefined ? true : iitem.enabled,
                        });
                    }
                }
            }

            this.appendChild(fgroup);
        }
    }

    /** Zobrazí ve filtrovacím panelu vstup pro pokročilý filtr */
    public showAdvancedFilter(): void {
        this._advancedFilter.showFilter();
    }

    protected _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(this.frameContext, cancellationToken);
    }

    public override dispose(): void {
        GenericControl.unregisterEventListener(this._advancedFilter, "filter");
        GenericControl.unregisterEventListener(this._advancedFilter, "filterChanged");
        this.filterSaveStateChanged.removeAll();
        this.frameConditionChanged.removeAll();
        this._advancedFilter?.dispose();
        this._attrNameToDataType?.clear();
        this.frameConditionParams = [];

        super.dispose();
    }
}

registerCustomElement("tsc-FilterBlock", FilterBlock);
